/**
 * Callable-safe helpers for letter template write payloads.
 * Firebase httpsCallable encodes data BEFORE fetch; unsafe values throw with no network.
 */

/**
 * Recursively assert a value is Firebase httpsCallable / JSON-safe.
 * Allowed: null, string, boolean, finite number, plain objects, arrays.
 * @param {unknown} value
 * @param {string} [path]
 * @returns {{ ok: true } | { ok: false, path: string, kind: string }}
 */
export function assertCallableSafeLetterTemplatePayload(value, path = 'root') {
  if (value === null) return { ok: true };
  const type = typeof value;
  if (type === 'string' || type === 'boolean') return { ok: true };
  if (type === 'number') {
    if (!Number.isFinite(value)) return { ok: false, path, kind: 'non_finite_number' };
    return { ok: true };
  }
  if (type === 'undefined') return { ok: false, path, kind: 'undefined' };
  if (type === 'bigint' || type === 'symbol' || type === 'function') {
    return { ok: false, path, kind: type };
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      const child = assertCallableSafeLetterTemplatePayload(value[i], `${path}[${i}]`);
      if (!child.ok) return child;
    }
    return { ok: true };
  }
  if (type === 'object') {
    // Reject DOM nodes / class instances / TipTap Editor objects.
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      return { ok: false, path, kind: 'non_plain_object' };
    }
    if (typeof value.nodeType === 'number' && typeof value.nodeName === 'string') {
      return { ok: false, path, kind: 'dom_node' };
    }
    for (const key of Object.keys(value)) {
      const child = assertCallableSafeLetterTemplatePayload(value[key], `${path}.${key}`);
      if (!child.ok) return child;
    }
    return { ok: true };
  }
  return { ok: false, path, kind: 'unknown' };
}

/**
 * Deep-clone via JSON to guarantee a plain, callable-safe structure.
 * Throws a typed Error with code `letter_template_payload_not_serializable` on failure.
 * @param {unknown} value
 */
export function cloneCallableSafeLetterTemplateJson(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    const err = new Error('letter_template_payload_not_serializable');
    err.code = 'letter_template_payload_not_serializable';
    throw err;
  }
}

/**
 * Staging/dev-only safe create diagnostic. Never logs payload/document/tokens/uids.
 * @param {{ stage: string, error: unknown, safeReason?: string | null }} args
 */
export function logLetterTemplateCreateDiagnostic({ stage, error, safeReason = null }) {
  const isProd = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD;
  const isStaging =
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    String(import.meta.env.VITE_FIREBASE_PROJECT_ID || '').includes('staging');
  if (isProd && !isStaging) return;

  const code =
    typeof error?.code === 'string'
      ? error.code
      : error?.code != null
        ? String(error.code)
        : null;
  const name = typeof error?.name === 'string' ? error.name : null;
  const message = typeof error?.message === 'string' ? error.message.slice(0, 160) : null;

  // eslint-disable-next-line no-console
  console.error('[letter-template-create]', {
    stage,
    code,
    name,
    message,
    safeReason,
  });
}
